const makeScene = (type, accent, elements = {}, animation = {}) => ({
  type,
  accent,
  elements,
  animation
});

const makeCore = (id, text, options, correct, gameType, scene) => ({
  id,
  text,
  options,
  correct,
  gameType,
  scene
});

const makeOcean = (id, text, options, gameType, scene) => ({
  id,
  text,
  options,
  gameType,
  scene
});

const o = (label, text) => ({ label, text });
const o4 = (a, b, c, d) => [o('A', a), o('B', b), o('C', c), o('D', d)];
const o5 = (a, b, c, d, e) => [o('A', a), o('B', b), o('C', c), o('D', d), o('E', e)];

const agreeScale = o5(
  'Strongly Disagree',
  'Disagree',
  'Neutral',
  'Agree',
  'Strongly Agree'
);

const pedagogicalFoundations = [
  makeCore(
    'ped_001',
    "A student understands facts but cannot apply them to new situations. Which approach best develops higher-order thinking?",
    o4(
      'Give more factual practice tests',
      "Use Bloom's Taxonomy - move from recall to analysis tasks",
      'Ask the student to memorize more content',
      'Reduce the complexity of topics covered'
    ),
    'B',
    'SCENARIO_PANEL',
    makeScene(
      'bloom_confusion',
      '#2a9d8f',
      {
        panel1: 'Teacher at whiteboard with pyramid diagram',
        panel2: 'Student confused at application question',
        panel3: 'Four choice cards'
      },
      {
        entry: 'Panels slide in from right sequentially, 500ms each',
        interaction: 'Cards fan out; selected card flips',
        outcome: 'Unselected cards slide off left'
      }
    )
  ),
  makeCore(
    'ped_002',
    'In this classroom layout, which seating arrangement best supports collaborative learning?',
    o4(
      'Traditional rows facing board',
      'Horseshoe/U-shape arrangement',
      'Individual isolated desks',
      'Lecture hall tiered seating'
    ),
    'B',
    'HOTSPOT_IMAGE',
    makeScene(
      'seating_quadrants',
      '#2a9d8f',
      {
        zones: [
          { id: 'A', x: 60, y: 80, width: 160, height: 100, label: 'Traditional Rows', description: 'Front-facing desks' },
          { id: 'B', x: 260, y: 80, width: 160, height: 100, label: 'U-Shape', description: 'Collaborative arrangement' },
          { id: 'C', x: 60, y: 200, width: 160, height: 100, label: 'Isolated Desks', description: 'Individual work stations' },
          { id: 'D', x: 260, y: 200, width: 160, height: 100, label: 'Lecture Hall', description: 'Tiered seating' }
        ],
        backgroundType: 'classroom_topdown'
      },
      {
        entry: 'SVG stroke draw + staggered hotspot fade',
        interaction: 'Hover glow, click zoom',
        feedback: 'Correct pulses with rings; wrong shakes'
      }
    )
  ),
  makeCore(
    'ped_003',
    "Complete this teaching principle: 'Learning is most effective when students _____ to _____ knowledge'",
    o4('listen / absorb', 'connect / prior', 'copy / rote', 'receive / passive'),
    'B',
    'WORD_BUILD',
    makeScene(
      'chalkboard_blanks',
      '#2a9d8f',
      {
        board: 'Chalkboard with two blank slots',
        cards: ['listen absorb', 'connect prior', 'copy rote', 'receive passive']
      },
      {
        entry: 'Board draws in with chalk stroke effect',
        interaction: 'Drag pair card into blanks',
        feedback: 'Correct chalk dust burst; wrong shakes and resets'
      }
    )
  ),
  makeCore(
    'ped_004',
    'How much class time should ideally be allocated to active student participation vs teacher talk?',
    o4(
      '10% student / 90% teacher',
      '30% student / 70% teacher',
      '60% student / 40% teacher',
      '90% student / 10% teacher'
    ),
    'C',
    'DIAL_TURN',
    makeScene(
      'participation_dial',
      '#2a9d8f',
      {
        dialZones: ['10/90', '30/70', '60/40', '90/10'],
        needleStart: 'center'
      },
      {
        entry: 'Dial sweep animation on load',
        interaction: 'Needle springs to clicked zone',
        feedback: 'Zone highlight + confirm pulse + lock click'
      }
    )
  ),
  makeCore(
    'ped_005',
    'Sort these teaching strategies into the correct learning theory they belong to:',
    o4(
      'Drill & practice -> Behaviourism',
      'Discovery learning -> Constructivism',
      'Social discussion -> Social Learning',
      'Lecture & recall -> Cognitivism'
    ),
    'B',
    'SORT_BIN',
    makeScene(
      'learning_theory_bins',
      '#2a9d8f',
      {
        bins: ['Behaviourism', 'Constructivism', 'Social Learning', 'Cognitivism'],
        cards: ['Drill & practice', 'Discovery learning', 'Social discussion', 'Lecture & recall']
      },
      {
        entry: 'Cards bounce in staggered',
        interaction: 'Drag cards into bins',
        feedback: 'Correct pop into bin; wrong wobble return'
      }
    )
  ),
  makeCore(
    'ped_006',
    "A student says: 'I already know this topic, why are we doing it again?' What is your best response?",
    o4(
      "Because it's in the syllabus",
      'Can you teach part of it to the class then?',
      'Please be quiet and follow along',
      "You'll understand why later"
    ),
    'B',
    'SPEECH_BUBBLE',
    makeScene(
      'student_pushback',
      '#2a9d8f',
      {
        studentLine: 'I already know this topic...',
        teacherBubble: 'empty_until_selected'
      },
      {
        entry: 'Student bubble types out',
        interaction: 'Response bubbles float in',
        feedback: 'Chosen text flows into teacher bubble + character reaction'
      }
    )
  ),
  makeCore(
    'ped_007',
    'Place these lesson phases in the correct instructional sequence:',
    o4('Assessment/Closure', 'Hook/Engagement', 'Guided Practice', 'Direct Instruction'),
    'B',
    'TIMELINE_DROP',
    makeScene(
      'lesson_flow_timeline',
      '#2a9d8f',
      {
        timelineSlots: [1, 2, 3, 4],
        cards: ['Assessment/Closure', 'Hook/Engagement', 'Guided Practice', 'Direct Instruction']
      },
      {
        entry: 'Timeline draws left-to-right',
        interaction: 'Drag cards to slots with magnetic snap',
        feedback: 'Correct advances timeline; wrong rejects card'
      }
    )
  ),
  makeCore(
    'ped_008',
    'Match each assessment type to its correct purpose:',
    o4(
      'Formative -> Monitor learning during instruction',
      'Summative -> Evaluate learning after instruction',
      'Diagnostic -> Identify prior knowledge gaps',
      'Ipsative -> Compare student to their own past'
    ),
    'A',
    'MEMORY_MATCH',
    makeScene(
      'assessment_pairs',
      '#2a9d8f',
      {
        pairs: [
          { id: 1, concept: 'Formative', definition: 'Monitor learning during instruction' },
          { id: 2, concept: 'Summative', definition: 'Evaluate learning after instruction' },
          { id: 3, concept: 'Diagnostic', definition: 'Identify prior knowledge gaps' },
          { id: 4, concept: 'Ipsative', definition: 'Compare to own past performance' }
        ],
        gridType: '2x4'
      },
      {
        entry: 'Cards fade in with slight tilt',
        interaction: 'Flip to match pairs',
        feedback: 'Match stays glowing; mismatch flips back after 800ms'
      }
    )
  ),
  makeCore(
    'ped_009',
    'Rate the importance of formative feedback in daily teaching practice:',
    o4(
      'Not important (0-25%)',
      'Somewhat important (26-50%)',
      'Very important (51-75%)',
      'Essential (76-100%)'
    ),
    'D',
    'METER_FILL',
    makeScene(
      'feedback_thermometer',
      '#2a9d8f',
      {
        meterType: 'vertical',
        zones: ['A', 'B', 'C', 'D']
      },
      {
        entry: 'Meter pre-fills demo sweep on load',
        interaction: 'Drag/click fill level',
        feedback: 'Zone lock + confirm button'
      }
    )
  ),
  makeCore(
    'ped_010',
    'Which teaching method is shown being used in this scene?',
    o4('Direct instruction', 'Peer teaching', 'Inquiry-based learning', 'Differentiated instruction'),
    'B',
    'SPOTLIGHT',
    makeScene(
      'dark_peer_scene',
      '#2a9d8f',
      {
        darkScene: 'students teaching each other',
        revealZones: ['direct', 'peer', 'inquiry', 'differentiated']
      },
      {
        entry: 'Scene starts dim',
        interaction: 'Spotlight follows cursor',
        feedback: 'Correct expands spotlight and illuminates full scene'
      }
    )
  )
];

const classroomSituationHandling = [
  makeCore(
    'cls_001',
    'A student at the back is disrupting others by talking loudly. Click the most effective first response:',
    o4(
      'Stop lesson, publicly call out the student',
      'Make eye contact and move closer to student',
      'Send the student out immediately',
      'Ignore and continue teaching'
    ),
    'B',
    'CHAOS_CLICK',
    makeScene(
      'backbench_disruption',
      '#457b9d',
      {
        zones: [
          { id: 'A', x: 80, y: 100, width: 120, height: 80, label: 'Public Callout', description: 'Stop and address loudly' },
          { id: 'B', x: 240, y: 180, width: 120, height: 80, label: 'Proximity', description: 'Move closer to student' },
          { id: 'C', x: 360, y: 80, width: 100, height: 60, label: 'Send Out', description: 'Remove from class' },
          { id: 'D', x: 360, y: 200, width: 100, height: 60, label: 'Ignore', description: 'Continue teaching' }
        ],
        disruptiveStudent: { zone: 'B', x: 280, y: 210 },
        backgroundType: 'classroom_topdown'
      },
      {
        entry: 'Speech waves pulse from disruptive student',
        interaction: 'Action hotspots glow sequence',
        feedback: 'Teacher glides to student on correct action'
      }
    )
  ),
  makeCore(
    'cls_002',
    'During an important exam, you notice two students sharing answers. You must choose immediately:',
    o4(
      'Stop exam, confront both publicly in front of class',
      'Quietly separate them, note it, report after exam',
      'Pretend not to notice to avoid disruption',
      'Cancel entire exam for the class'
    ),
    'B',
    'TROLLEY',
    makeScene(
      'exam_cheating_split',
      '#457b9d',
      {
        scene: 'exam hall with two highlighted desks',
        tracks: ['public confrontation', 'quiet intervention']
      },
      {
        entry: 'Track lines draw + lever pulse',
        interaction: 'Choose intervention track',
        feedback: 'Classroom reaction consequence'
      }
    )
  ),
  makeCore(
    'cls_003',
    "A parent messages: 'My child says you embarrassed them in class. I want this resolved.' What is your first response?",
    o4(
      'Your child was misbehaving, I had no choice',
      "I'd like to meet and discuss what happened",
      'Please speak to the principal about this',
      'I treat all students fairly, this is not an issue'
    ),
    'B',
    'SPEECH_BUBBLE',
    makeScene(
      'parent_message_phone',
      '#457b9d',
      {
        incomingMessage: 'parent complaint',
        responseBubbles: 4
      },
      {
        entry: 'Phone slides from top + message types out',
        interaction: 'Select and send response bubble',
        feedback: 'Parent reaction emoji appears'
      }
    )
  ),
  makeCore(
    'cls_004',
    'Sort these classroom behaviours into the correct management response category:',
    o4(
      'Minor distraction -> Non-verbal cue',
      'Repeated disruption -> Private verbal warning',
      'Defiance -> Structured consequence',
      'Aggression -> Immediate escalation'
    ),
    'D',
    'SORT_BIN',
    makeScene(
      'behavior_response_bins',
      '#457b9d',
      {
        bins: ['Handle Yourself', 'Warn', 'Escalate'],
        behaviors: ['minor distraction', 'repeated disruption', 'defiance', 'aggression']
      },
      {
        entry: 'Behavior cards slide in left',
        interaction: 'Drag to bins',
        feedback: 'Correct snap; wrong bounce-out'
      }
    )
  ),
  makeCore(
    'cls_005',
    'You have a student who consistently finishes work 10 minutes early and then distracts others. What is your strategy?',
    o4(
      'Give them extra worksheets to keep busy',
      'Design extension tasks that deepen learning',
      'Ask them to help slower students (without training)',
      'Tell them to read quietly until others finish'
    ),
    'B',
    'SCENARIO_PANEL',
    makeScene(
      'early_finisher',
      '#457b9d',
      {
        panel1: 'Fast student bored',
        panel2: 'Begins distracting neighbor',
        panel3: 'Strategy choices'
      },
      {
        entry: 'Comic borders draw and page-flip transition',
        interaction: 'Cards dealt into panel 3',
        feedback: 'Selection confirmation pulse'
      }
    )
  ),
  makeCore(
    'cls_006',
    'In this seating arrangement, identify the student who is most likely experiencing social isolation:',
    o4(
      'Student at front centre',
      'Student in back corner alone',
      'Student in middle of group',
      'Student near the window with one neighbor'
    ),
    'B',
    'HOTSPOT_IMAGE',
    makeScene(
      'isolation_map',
      '#457b9d',
      {
        layout: '15 desks with social connection lines',
        clue: 'isolated student has no connection lines'
      },
      {
        entry: 'Desks appear staggered',
        interaction: 'Hover highlights student zones',
        feedback: 'Camera zoom to selected student'
      }
    )
  ),
  makeCore(
    'cls_007',
    'You have 5 minutes before class ends and these tasks remain. Order them by priority:',
    o4(
      'Assign homework clearly',
      'Summarize key lesson points',
      "Answer a student's detailed question",
      'Collect submitted worksheets'
    ),
    'B',
    'PRIORITY_DRAG',
    makeScene(
      'end_of_class_priority',
      '#457b9d',
      {
        cards: ['Assign homework', 'Summarize lesson', 'Answer question', 'Collect worksheets'],
        timeline: '5 minutes'
      },
      {
        entry: 'Cards float in with ticking clock',
        interaction: 'Reorder by drag',
        feedback: 'Submit converts to ranked list'
      }
    )
  ),
  makeCore(
    'cls_008',
    'How firm should classroom rules be in the first week of school?',
    o4(
      'Very flexible, let students find their way (0-25%)',
      'Somewhat flexible with some structure (26-50%)',
      'Clear and consistent with some warmth (51-75%)',
      'Strict with zero tolerance immediately (76-100%)'
    ),
    'C',
    'METER_FILL',
    makeScene(
      'strictness_meter',
      '#457b9d',
      {
        meter: 'strictness',
        linkedIndicator: 'classroom harmony'
      },
      {
        entry: 'Meter and harmony indicator sync',
        interaction: 'Fill level sets style',
        feedback: 'Chaos/calm/tense visual response'
      }
    )
  ),
  makeCore(
    'cls_009',
    "Complete the behaviour management principle: 'Always address the _____, not the _____'",
    o4('student / problem', 'behaviour / person', 'parent / teacher', 'rule / exception'),
    'B',
    'WORD_BUILD',
    makeScene(
      'behavior_principle_notepad',
      '#457b9d',
      {
        template: 'Always address the [ ] not the [ ]',
        noteStyle: 'sticky notes'
      },
      {
        entry: 'Notepad tears in + sticky notes flutter',
        interaction: 'Drag pair into blanks',
        feedback: 'Correct sticks with ink line animation'
      }
    )
  ),
  makeCore(
    'cls_010',
    'Place these stages of a restorative conversation in the correct order:',
    o4(
      'Agree on a repair action',
      'Hear what happened from student',
      'Explore impact on others',
      'Check in and follow up later'
    ),
    'B',
    'TIMELINE_DROP',
    makeScene(
      'restorative_wheel',
      '#457b9d',
      {
        wheel: 'circular 4-stage process',
        directionArrow: true
      },
      {
        entry: 'Wheel rotates slowly',
        interaction: 'Drop stage markers in order',
        feedback: 'Complete wheel celebration spin'
      }
    )
  )
];
const digitalLiteracyEdtech = [
  makeCore(
    'dig_001',
    "A teacher shares a student's test score publicly on the class screen. Identify the problem:",
    o4(
      'The font size is too small for the class to read',
      'Student data privacy has been violated',
      'The score display format is incorrect',
      'The screen brightness is too high'
    ),
    'B',
    'CHAOS_CLICK',
    makeScene(
      'privacy_violation_screen',
      '#6a4c93',
      {
        zones: [
          { id: 'A', x: 60, y: 40, width: 180, height: 40, label: 'Browser Tabs', description: 'Multiple open tabs' },
          { id: 'B', x: 340, y: 100, width: 120, height: 80, label: 'Visible Scores', description: 'Student data displayed publicly' },
          { id: 'C', x: 60, y: 90, width: 260, height: 180, label: 'School Website', description: 'Standard web content' },
          { id: 'D', x: 340, y: 200, width: 120, height: 70, label: 'Display Panel', description: 'Settings area' }
        ],
        warningZone: { id: 'B', type: 'privacy_violation' },
        backgroundType: 'browser_screen'
      },
      {
        entry: 'Screen flickers on',
        interaction: 'Click problem zone',
        feedback: 'Correct shows red policy border'
      }
    )
  ),
  makeCore(
    'dig_002',
    'Move the spotlight to find the most appropriate tool for collaborative student document editing:',
    o4('WhatsApp group', 'Google Docs shared link', 'Email attachment chain', 'Printed handout with notes'),
    'B',
    'SPOTLIGHT',
    makeScene(
      'tool_landscape_dark',
      '#6a4c93',
      {
        icons: ['WhatsApp', 'Google Docs', 'Email chain', 'Handout'],
        correctHotspot: 'Google Docs'
      },
      {
        entry: 'Icons hidden until spotlit',
        interaction: 'Spotlight sweep and click',
        feedback: 'Selected tool zooms forward'
      }
    )
  ),
  makeCore(
    'dig_003',
    'Sort these EdTech tools into their primary use category:',
    o4(
      'Kahoot -> Gamified Assessment',
      'Google Classroom -> LMS/Course Management',
      'Canva -> Visual Content Creation',
      'Zoom -> Video Communication'
    ),
    'B',
    'SORT_BIN',
    makeScene(
      'tool_category_bins',
      '#6a4c93',
      {
        bins: ['Gamified Assessment', 'LMS', 'Visual Creation', 'Video Communication'],
        toolCards: ['Kahoot', 'Google Classroom', 'Canva', 'Zoom']
      },
      {
        entry: 'App-like cards bounce in',
        interaction: 'Drag to category bins',
        feedback: 'All correct lights up sequence'
      }
    )
  ),
  makeCore(
    'dig_004',
    'A student submits an essay that appears to be entirely AI-generated. What do you do?',
    o4(
      'Give zero marks with no discussion',
      'Have a learning conversation about AI use and integrity',
      'Ignore it as AI use is normal now',
      'Report immediately to principal without speaking to student'
    ),
    'B',
    'SCENARIO_PANEL',
    makeScene(
      'ai_essay_flag',
      '#6a4c93',
      {
        panel1: 'Student submits from laptop',
        panel2: 'AI flag appears',
        panel3: 'Action choices'
      },
      {
        entry: 'Keyboard typing then AI flag glitch',
        interaction: 'Choose response card',
        feedback: 'Tech-style confirmation'
      }
    )
  ),
  makeCore(
    'dig_005',
    "Complete the digital citizenship rule: 'Think before you _____ - once online, content is _____'",
    o4('speak / forgotten', 'post / permanent', 'share / private', 'delete / gone'),
    'B',
    'WORD_BUILD',
    makeScene(
      'digital_citizenship_post',
      '#6a4c93',
      {
        template: 'Think before you [ ] once online content is [ ]',
        chips: ['speak/forgotten', 'post/permanent', 'share/private', 'delete/gone']
      },
      {
        entry: 'Social post UI slides in',
        interaction: 'Drag chips into blanks',
        feedback: 'Correct sends post animation'
      }
    )
  ),
  makeCore(
    'dig_006',
    "Look at this student's screen setup. Click what violates safe internet practice:",
    o4(
      'Multiple browser tabs open',
      'Password written on sticky note beside screen',
      'School website visible in browser',
      'Screen brightness setting panel open'
    ),
    'B',
    'HOTSPOT_IMAGE',
    makeScene(
      'desk_security_view',
      '#6a4c93',
      {
        desk: 'monitor, keyboard, sticky note with password123',
        hotspots: ['tabs', 'sticky_password', 'school_site', 'brightness']
      },
      {
        entry: 'Layered desk scene renders',
        interaction: 'Hover glow + click zoom',
        feedback: 'Warning icon on correct selection'
      }
    )
  ),
  makeCore(
    'dig_007',
    'Set the appropriate screen time for a 60-minute class using EdTech tools:',
    o4(
      '60 minutes (full class on screens)',
      '40-50 minutes (screen dominant)',
      '20-30 minutes (balanced screen/offline)',
      '0-10 minutes (minimal screen)'
    ),
    'C',
    'DIAL_TURN',
    makeScene(
      'screen_time_dial',
      '#6a4c93',
      {
        dial: 'screen-time zones',
        linkedMeter: 'student wellbeing'
      },
      {
        entry: 'Dial + wellbeing animate together',
        interaction: 'Turn dial and release',
        feedback: 'Balanced zone shows engaged student'
      }
    )
  ),
  makeCore(
    'dig_008',
    'Match each digital tool to its pedagogical purpose:',
    o4(
      'Padlet -> Collaborative brainstorming board',
      'Quizlet -> Flashcard-based memorization',
      'Flipgrid -> Video reflection and discussion',
      'Mentimeter -> Live audience polling'
    ),
    'A',
    'MEMORY_MATCH',
    makeScene(
      'edtech_pair_match',
      '#6a4c93',
      {
        grid: '8 cards, circuit-pattern backs',
        pairs: ['Padlet', 'Quizlet', 'Flipgrid', 'Mentimeter']
      },
      {
        entry: 'Circuit pattern pulses',
        interaction: 'Flip and match',
        feedback: 'Matched cards connect with animated line'
      }
    )
  ),
  makeCore(
    'dig_009',
    'You want to use a new AI tool in class. The school has no clear policy yet. You:',
    o4(
      'Use it anyway - it benefits students',
      'Raise it with administration before using',
      'Let students use it unsupervised',
      'Avoid all technology until policy exists'
    ),
    'B',
    'TROLLEY',
    makeScene(
      'ai_policy_fork',
      '#6a4c93',
      {
        tracks: 'circuit-board fork with switch',
        endpoints: ['school policy', 'classroom usage']
      },
      {
        entry: 'Circuit traces draw in',
        interaction: 'Route data packet path',
        feedback: 'Electrical spark at switch point'
      }
    )
  ),
  makeCore(
    'dig_010',
    'Order these steps for evaluating a new EdTech tool before classroom use:',
    o4(
      'Pilot with a small student group',
      'Check data privacy and student safety compliance',
      'Research peer reviews and pedagogy alignment',
      'Get administration and parent approval'
    ),
    'B',
    'PRIORITY_DRAG',
    makeScene(
      'tool_eval_board',
      '#6a4c93',
      {
        board: 'decision board with slots 1-4',
        cards: ['pilot', 'privacy compliance', 'peer review', 'approval']
      },
      {
        entry: 'Cards drop onto board',
        interaction: 'Reorder by drag',
        feedback: 'Correct sequence stamps APPROVED'
      }
    )
  )
];
const subjectAcademicKnowledge = [
  makeCore(
    'sub_001',
    "A student says: 'Plants make their own food from sunlight - does that mean they don't need soil at all?' How do you respond?",
    o4(
      'Yes, soil is not needed at all',
      'Soil provides water and minerals that support growth',
      'That question is off-topic, stay focused',
      'Read the textbook chapter on photosynthesis'
    ),
    'B',
    'SPEECH_BUBBLE',
    makeScene(
      'photosynthesis_query',
      '#f4a261',
      {
        background: 'plant, sun, soil',
        studentBubble: 'concept misunderstanding'
      },
      {
        entry: 'Speech bubble handwrites in',
        interaction: 'Select teacher response bubble',
        feedback: 'Selected response grows into teacher bubble'
      }
    )
  ),
  makeCore(
    'sub_002',
    'In this diagram, click the part of the cell responsible for energy production:',
    o4('Nucleus', 'Mitochondria', 'Cell membrane', 'Vacuole'),
    'B',
    'HOTSPOT_IMAGE',
    makeScene(
      'cell_organelle_map',
      '#f4a261',
      {
        diagram: 'labeled cell with clickable organelles',
        hotspots: ['nucleus', 'mitochondria', 'membrane', 'vacuole']
      },
      {
        entry: 'Cell diagram stroke-draw',
        interaction: 'Hover enlarge and tooltip',
        feedback: 'Mitochondria emits energy sparks'
      }
    )
  ),
  makeCore(
    'sub_003',
    'Place these historical events in chronological order:',
    o4('Industrial Revolution begins', 'World War I starts', 'French Revolution', 'First Moon Landing'),
    'C',
    'TIMELINE_DROP',
    makeScene(
      'history_timeline',
      '#f4a261',
      {
        timeline: 'unrolling horizontal dates',
        cards: ['Industrial Revolution', 'WWI', 'French Revolution', 'Moon Landing']
      },
      {
        entry: 'Timeline scroll unrolls from left',
        interaction: 'Drag event cards to anchors',
        feedback: 'Correct sequence glows gold'
      }
    )
  ),
  makeCore(
    'sub_004',
    "Complete the mathematical principle: 'The sum of angles in any _____ always equals _____ degrees'",
    o4('circle / 360', 'triangle / 180', 'square / 90', 'polygon / 270'),
    'B',
    'WORD_BUILD',
    makeScene(
      'math_angle_equation',
      '#f4a261',
      {
        board: 'equation with two blanks',
        shapeHints: ['circle', 'triangle', 'square', 'polygon']
      },
      {
        entry: 'Geometric shapes rotate with angle arcs',
        interaction: 'Drag chips into blanks',
        feedback: 'Correct proves with animated angle lines'
      }
    )
  ),
  makeCore(
    'sub_005',
    'A student submits a science project with a conclusion that contradicts their data. What is the best teaching response?',
    o4(
      'Mark it wrong without explanation',
      'Guide them to compare their data and conclusion using inquiry questions',
      'Rewrite the conclusion for them',
      'Pass it since effort was shown'
    ),
    'B',
    'SCENARIO_PANEL',
    makeScene(
      'data_conclusion_mismatch',
      '#f4a261',
      {
        panel1: 'Student submits project',
        panel2: 'Data vs conclusion mismatch',
        panel3: 'Response approaches'
      },
      {
        entry: 'Panels slide with mismatch arrow pulse',
        interaction: 'Select response card',
        feedback: 'Consequence reveal'
      }
    )
  ),
  makeCore(
    'sub_006',
    'Match each literary device to the correct example:',
    o4(
      'Metaphor -> "Life is a journey"',
      'Simile -> "Fast as lightning"',
      'Alliteration -> "Peter Piper picked..."',
      'Personification -> "The wind whispered"'
    ),
    'A',
    'MEMORY_MATCH',
    makeScene(
      'literary_device_books',
      '#f4a261',
      {
        theme: 'book pages in library style',
        pairs: ['Metaphor', 'Simile', 'Alliteration', 'Personification']
      },
      {
        entry: 'Cards appear as book pages',
        interaction: 'Flip/page-turn to match',
        feedback: 'Matched pages bind together'
      }
    )
  ),
  makeCore(
    'sub_007',
    'Sort these number types into the correct mathematical category:',
    o4('√2 -> Irrational', '3/4 -> Rational', '-7 -> Integer', '3.14159... -> Irrational'),
    'A',
    'SORT_BIN',
    makeScene(
      'number_type_bins',
      '#f4a261',
      {
        bins: ['Irrational', 'Rational', 'Integer'],
        cards: ['√2', '3/4', '-7', '3.14159...']
      },
      {
        entry: 'Number line animates in',
        interaction: 'Drag card to category',
        feedback: 'Correct symbol burst; wrong bounce back'
      }
    )
  ),
  makeCore(
    'sub_008',
    'In this chemical equation, click the element that is NOT balanced correctly:',
    o4('Hydrogen (H)', 'Oxygen (O) - unbalanced', 'Carbon (C)', 'Nitrogen (N)'),
    'B',
    'CHAOS_CLICK',
    makeScene(
      'equation_balance_scan',
      '#f4a261',
      {
        whiteboard: 'chemical equation with clickable element zones',
        subtleCue: 'unbalanced element slight wobble'
      },
      {
        entry: 'Equation writes itself',
        interaction: 'Click element group',
        feedback: 'Magnified mismatch on correct element'
      }
    )
  ),
  makeCore(
    'sub_009',
    "Move the spotlight across this world map to find the country known as the 'Land of the Rising Sun':",
    o4('China', 'South Korea', 'Japan', 'Thailand'),
    'C',
    'SPOTLIGHT',
    makeScene(
      'asia_spotlight_map',
      '#f4a261',
      {
        map: 'dark Asia region',
        revealHint: 'Japan shows rising sun icon when illuminated'
      },
      {
        entry: 'Map starts dark',
        interaction: 'Move spotlight and click target',
        feedback: 'Rising sun animation on Japan'
      }
    )
  ),
  makeCore(
    'sub_010',
    'Set the difficulty level appropriate for students who are just beginning a new topic:',
    o4(
      'Expert level immediately (challenge them)',
      'Above grade level (stretch)',
      'At grade level (appropriate entry)',
      'Below grade level (build confidence first, then scaffold up)'
    ),
    'C',
    'DIAL_TURN',
    makeScene(
      'zpd_difficulty_dial',
      '#f4a261',
      {
        dial: 'difficulty zones around ZPD rings',
        avatar: 'student confidence indicator'
      },
      {
        entry: 'ZPD rings pulse on load',
        interaction: 'Turn difficulty dial',
        feedback: 'Optimal zone glows with confident avatar'
      }
    )
  )
];
const ethicsPolicyLeadership = [
  makeCore(
    'eth_001',
    "A colleague asks you to sign off on student attendance you didn't witness. Pulling the lever = sign it. Doing nothing = refuse.",
    o4(
      "Sign - it's probably fine, avoid conflict",
      'Refuse - integrity cannot be compromised',
      'Sign but add a note of uncertainty',
      'Report colleague to administration immediately'
    ),
    'B',
    'TROLLEY',
    makeScene(
      'attendance_register_split',
      '#e63946',
      {
        tracks: ['signed register', 'unsigned register'],
        lever: 'pen'
      },
      {
        entry: 'Register document unfolds',
        interaction: 'Pen lever route choice',
        feedback: 'Signature animation or REFUSED stamp'
      }
    )
  ),
  makeCore(
    'eth_002',
    'You discover a policy in your school manual that you believe is unfair to students. What is the ethical course of action?',
    o4(
      'Ignore it - policy is policy',
      'Quietly break the rule when convenient',
      'Raise it formally through proper channels',
      'Complain to students about it'
    ),
    'C',
    'SCENARIO_PANEL',
    makeScene(
      'policy_fairness',
      '#e63946',
      {
        panel1: 'Teacher reading policy manual',
        panel2: 'Justice scale imbalance',
        panel3: 'Formal action choices'
      },
      {
        entry: 'Manual flips pages',
        interaction: 'Choose action card',
        feedback: 'Scale stabilizes on ethical action'
      }
    )
  ),
  makeCore(
    'eth_003',
    'Order these professional responsibilities by ethical priority (most important first):',
    o4(
      'Student safety and wellbeing',
      'Curriculum delivery',
      'Institutional reputation',
      'Personal convenience'
    ),
    'A',
    'PRIORITY_DRAG',
    makeScene(
      'ethics_priority_stack',
      '#e63946',
      {
        cards: ['Student safety', 'Curriculum delivery', 'Institutional reputation', 'Personal convenience'],
        scale: 'ethics balance'
      },
      {
        entry: 'Weighted cards settle in stack',
        interaction: 'Reorder by ethical priority',
        feedback: 'Balanced scale on correct order'
      }
    )
  ),
  makeCore(
    'eth_004',
    'A student confides they are being bullied by another teacher. You respond:',
    o4(
      'Are you sure? That teacher is very respected',
      'I believe you. I will help you report this safely',
      "Keep it between us, I'll handle it quietly",
      'You should speak to your parents first'
    ),
    'B',
    'SPEECH_BUBBLE',
    makeScene(
      'safeguarding_confidence',
      '#e63946',
      {
        studentMood: 'worried',
        meter: 'trust meter visible'
      },
      {
        entry: 'Anxious bubble tremble',
        interaction: 'Select teacher response',
        feedback: 'Trust meter rises/falls based on choice'
      }
    )
  ),
  makeCore(
    'eth_005',
    'In this staff meeting scene, identify the behaviour that violates professional ethics:',
    o4(
      'Teacher taking notes',
      'Teacher on phone sharing student info',
      'Teacher asking a question',
      'Teacher reviewing documents'
    ),
    'B',
    'HOTSPOT_IMAGE',
    makeScene(
      'staff_meeting_breach',
      '#e63946',
      {
        room: 'five staff figures',
        breachCue: 'phone screen with student data'
      },
      {
        entry: 'Ambient meeting motion',
        interaction: 'Click staff hotspot',
        feedback: 'DATA BREACH overlay on correct figure'
      }
    )
  ),
  makeCore(
    'eth_006',
    'Sort these teacher actions into ethical or unethical categories:',
    o4(
      'Sharing student medical info in staff room -> Unethical',
      'Reporting suspected child abuse -> Ethical',
      'Accepting gifts from parents to boost grades -> Unethical',
      'Providing extra support to struggling students -> Ethical'
    ),
    'B',
    'SORT_BIN',
    makeScene(
      'ethical_unethical_bins',
      '#e63946',
      {
        bins: ['ETHICAL', 'UNETHICAL'],
        scenarios: 4
      },
      {
        entry: 'Cards flip scenario-side up on drag',
        interaction: 'Drag into ethical bins',
        feedback: 'Justice scale animation on completion'
      }
    )
  ),
  makeCore(
    'eth_007',
    'A gifted student needs enrichment but resources are limited. Another student needs remediation. You have one free period. Choose:',
    o4(
      'Spend it with the gifted student (enrichment)',
      'Spend it with the struggling student (remediation)',
      'Split it equally between both',
      'Use it for your own planning'
    ),
    'C',
    'TROLLEY',
    makeScene(
      'resource_allocation_fork',
      '#e63946',
      {
        tracks: ['enrichment room', 'remediation room'],
        timer: 'one free period'
      },
      {
        entry: 'Both student endpoints animate request',
        interaction: 'Route teacher trolley',
        feedback: 'Outcome emotion on both ends'
      }
    )
  ),
  makeCore(
    'eth_008',
    'How transparent should a teacher be with parents about classroom incidents?',
    o4(
      'Share nothing - handle internally (0-25%)',
      'Share only serious incidents (26-50%)',
      'Share all relevant incidents with context (51-75%)',
      'Share everything including minor incidents (76-100%)'
    ),
    'C',
    'METER_FILL',
    makeScene(
      'trust_bridge_meter',
      '#e63946',
      {
        bridge: 'school-home trust bridge',
        meter: 'transparency level'
      },
      {
        entry: 'Bridge builds with meter fill',
        interaction: 'Set transparency level',
        feedback: 'Optimal zone shows connected figures'
      }
    )
  ),
  makeCore(
    'eth_009',
    "Complete the safeguarding principle: 'The _____ of the child is always the _____ consideration'",
    o4('opinion / last', 'wellbeing / primary', 'grade / only', 'behaviour / first'),
    'B',
    'WORD_BUILD',
    makeScene(
      'safeguarding_document',
      '#e63946',
      {
        template: 'The [ ] of the child is always the [ ] consideration',
        seal: 'official seal corner'
      },
      {
        entry: 'Document unfolds formally',
        interaction: 'Place word chips',
        feedback: 'COMPLIANT stamp on correct phrase'
      }
    )
  ),
  makeCore(
    'eth_010',
    'In this school newsletter draft, click the content that should NOT be published:',
    o4(
      'Upcoming sports event details',
      'Student photo without consent form',
      'New library book arrivals',
      'Teacher of the month announcement'
    ),
    'B',
    'CHAOS_CLICK',
    makeScene(
      'newsletter_review',
      '#e63946',
      {
        layout: 'newsletter with four content blocks',
        violation: 'student photo without consent'
      },
      {
        entry: 'Newsletter pages slide in',
        interaction: 'Click content block',
        feedback: 'GDPR warning + lock icon'
      }
    )
  )
];
const oceanOpenness = [
  makeOcean(
    'opn_001',
    'I actively seek out new teaching methods even when my current approach is working well.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'innovation_pull',
      '#2a9d8f',
      {
        panel1: 'Standard lesson working well',
        panel2: 'New innovative method appears',
        panel3: 'Agreement cards A-E'
      },
      {
        entry: 'Panels reveal with curiosity compass',
        interaction: 'Choose A-E scale card',
        feedback: 'Compass needle shifts with selection'
      }
    )
  ),
  makeOcean(
    'opn_002',
    'When a student challenges my explanation with a different perspective, I find it stimulating.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'perspective_challenge',
      '#2a9d8f',
      {
        panel1: 'Student raises alternative view',
        panel2: 'Two thought bubbles',
        panel3: 'A-E response'
      },
      {
        entry: 'Thought bubbles separate and merge',
        interaction: 'Select agreement level',
        feedback: 'Bubble merge strength reflects openness'
      }
    )
  ),
  makeOcean(
    'opn_003',
    "Rate how much this describes you: 'I enjoy exploring topics outside my subject area for inspiration'",
    agreeScale,
    'WORD_BUILD',
    makeScene(
      'knowledge_islands',
      '#2a9d8f',
      {
        map: 'subject islands with bridges',
        scale: 'A-E curiosity continuum'
      },
      {
        entry: 'Islands fade in',
        interaction: 'Pick scale option',
        feedback: 'More bridges appear toward higher agreement'
      }
    )
  ),
  makeOcean(
    'opn_004',
    'I feel uncomfortable when the school introduces a completely new curriculum framework.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'new_framework_response',
      '#2a9d8f',
      {
        panel1: 'School announces new framework',
        panel2: 'Change documents pile',
        panel3: 'Comfort scale A-E'
      },
      {
        entry: 'Announcement slides in',
        interaction: 'Choose comfort level',
        feedback: 'Storm/sun ambience shifts'
      }
    )
  ),
  makeOcean(
    'opn_005',
    'Move the spotlight to the teaching approach that most excites you:',
    o5(
      'Tried and tested textbook method',
      'Student-led inquiry projects',
      'Standardised test preparation',
      "Repeat last year's lesson plans",
      'Cross-curricular collaborative units'
    ),
    'SPOTLIGHT',
    makeScene(
      'approach_spotlight',
      '#2a9d8f',
      {
        darkRoomIcons: 5,
        creativeZones: ['inquiry', 'cross_curricular']
      },
      {
        entry: 'Approach icons hidden in dark scene',
        interaction: 'Spotlight reveal and click',
        feedback: 'Creative zones glow warmer'
      }
    )
  ),
  makeOcean(
    'opn_006',
    'I regularly read educational research to evolve my teaching practice.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'research_habit',
      '#2a9d8f',
      {
        panel1: 'Research journals on desk',
        panel2: 'Reading vs passive alternative',
        panel3: 'Frequency scale A-E'
      },
      {
        entry: 'Journal stack animates in',
        interaction: 'A-E selection',
        feedback: 'Knowledge bar fill'
      }
    )
  ),
  makeOcean(
    'opn_007',
    'I would volunteer to pilot a completely untested teaching experiment in my class.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'pilot_experiment',
      '#2a9d8f',
      {
        panel1: 'Admin presents experimental program',
        panel2: 'Risk-reward scale',
        panel3: 'Willingness A-E'
      },
      {
        entry: 'Risk-reward scale appears',
        interaction: 'Set willingness level',
        feedback: 'Scale tips toward risk/safety'
      }
    )
  ),
  makeOcean(
    'opn_008',
    'Match how you typically respond to novel teaching situations:',
    agreeScale,
    'MEMORY_MATCH',
    makeScene(
      'novelty_response_pairs',
      '#2a9d8f',
      {
        cards: 'situation-response pair matching',
        insightCard: 'revealed at completion'
      },
      {
        entry: 'Cards shuffle and settle',
        interaction: 'Flip/match pairs',
        feedback: 'Matched pairs reveal openness insight'
      }
    )
  ),
  makeOcean(
    'opn_009',
    'When visiting another school, I pay close attention to practices different from my own.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'school_visit_observation',
      '#2a9d8f',
      {
        panel1: 'Teacher walking through unfamiliar school',
        panel2: 'Different methods in use',
        panel3: 'Interest scale A-E'
      },
      {
        entry: 'Camera zoom from hallway to classroom',
        interaction: 'Select interest level',
        feedback: 'Observation lens sharpen effect'
      }
    )
  ),
  makeOcean(
    'opn_010',
    'Rate how often you incorporate student ideas into your lesson design:',
    o5('Never', 'Rarely', 'Sometimes', 'Often', 'Always'),
    'LIKERT_SLIDER',
    makeScene(
      'idea_funnel',
      '#2a9d8f',
      {
        funnel: 'student ideas into lesson plan',
        scale: 'A never -> E always'
      },
      {
        entry: 'Funnel outline draws in',
        interaction: 'Slider or scale selection',
        feedback: 'Funnel fill rises by level'
      }
    )
  )
];
const oceanConscientiousness = [
  makeOcean(
    'con_001',
    "It's Sunday evening. Drag these tasks into the order you would actually complete them:",
    o5(
      "Plan Monday's lessons in detail",
      "Mark last week's assignments",
      'Reply to parent emails',
      'Review student progress data',
      'Organise your physical classroom'
    ),
    'PRIORITY_DRAG',
    makeScene(
      'sunday_task_stack',
      '#f4a261',
      {
        board: 'task manager with urgency indicators',
        slots: 'Most important to least important'
      },
      {
        entry: 'Cards rain onto board',
        interaction: 'Reorder tasks by drag',
        feedback: 'Priority order lock-in'
      }
    )
  ),
  makeOcean(
    'con_002',
    'How detailed is your typical lesson plan?',
    o5(
      'Very rough notes',
      'Basic outline only',
      'Moderately detailed',
      'Detailed with key checkpoints',
      'Fully detailed with timings and contingencies'
    ),
    'METER_FILL',
    makeScene(
      'lesson_plan_detail_meter',
      '#f4a261',
      {
        document: 'plan fills with increasing detail',
        zones: ['A', 'B', 'C', 'D', 'E']
      },
      {
        entry: 'Blank template appears',
        interaction: 'Set detail meter',
        feedback: 'Sections auto-populate by level'
      }
    )
  ),
  makeOcean(
    'con_003',
    'You have a report deadline tomorrow. Order your evening tasks honestly:',
    o5(
      'Start the report immediately',
      'Have dinner and then start',
      'Watch one episode then start',
      'Do it first thing tomorrow morning',
      'Ask for an extension'
    ),
    'PRIORITY_DRAG',
    makeScene(
      'deadline_evening',
      '#f4a261',
      {
        cards: 'deadline choices',
        clock: 'ticks faster as procrastination increases'
      },
      {
        entry: 'Deadline clock fades in',
        interaction: 'Reorder likely behavior',
        feedback: 'Top-ranked action becomes recorded response'
      }
    )
  ),
  makeOcean(
    'con_004',
    'These notifications arrive simultaneously. Which do you open first?',
    o5(
      'Parent complaint about grades (urgent)',
      'Staff meeting agenda for next week',
      'Student submitted late assignment',
      'Newsletter from education department',
      'Social message from colleague'
    ),
    'STRESS_INBOX',
    makeScene(
      'morning_notification_stack',
      '#f4a261',
      {
        inboxType: 'organisational instinct check',
        stagger: 'notifications flood in'
      },
      {
        entry: 'Cards slide in one-by-one',
        interaction: 'Open first item to choose',
        feedback: 'Choice locks after timing gate'
      }
    )
  ),
  makeOcean(
    'con_005',
    'Sort these work habits as ones you Do Consistently vs Do Rarely:',
    o5(
      'I plan tasks before starting',
      'I batch shallow work into fixed windows',
      'I leave deadlines to the last minute',
      'I review progress checkpoints weekly',
      'I rely on memory instead of systems'
    ),
    'SORT_BIN',
    makeScene(
      'habit_reflection_bins',
      '#f4a261',
      {
        bins: ['Do Consistently', 'Do Rarely'],
        cards: 'work-habit statements'
      },
      {
        entry: 'Cards float with mirror effect',
        interaction: 'Drag to habit bins',
        feedback: 'Energy tint confirms placement'
      }
    )
  ),
  makeOcean(
    'con_006',
    'Place your typical assignment marking timeline on this scale:',
    o5(
      'Day before deadline',
      'Night before submission',
      'Within 2-3 days',
      'Within 24 hours',
      'Same day received'
    ),
    'TIMELINE_DROP',
    makeScene(
      'marking_timeline',
      '#f4a261',
      {
        weekScale: true,
        marker: 'drag to typical position'
      },
      {
        entry: 'Timeline appears with date ticks',
        interaction: 'Drop marker on preferred slot',
        feedback: 'Recorded slot maps to A-E'
      }
    )
  ),
  makeOcean(
    'con_007',
    'How organised is your physical workspace right now?',
    o5('Complete chaos', 'Mostly cluttered', 'Mixed', 'Mostly organised', 'Perfectly organised'),
    'DIAL_TURN',
    makeScene(
      'workspace_organization_dial',
      '#f4a261',
      {
        deskStates: ['chaos', 'cluttered', 'mixed', 'organized', 'pristine']
      },
      {
        entry: 'Desk scene loads cluttered',
        interaction: 'Turn dial',
        feedback: 'Desk auto-organizes as dial rises'
      }
    )
  ),
  makeOcean(
    'con_008',
    'A project has 5 sub-tasks. How do you naturally approach it?',
    o5(
      'List all tasks first',
      'Start with what interests me',
      'Start with the hardest part',
      'Start with the easiest part',
      'Work on all parts a little each day'
    ),
    'PRIORITY_DRAG',
    makeScene(
      'project_work_pattern',
      '#f4a261',
      {
        board: 'project board with five strategies',
        indicator: 'pattern efficiency meter'
      },
      {
        entry: 'Board cards fan in',
        interaction: 'Drag rank your likely approach',
        feedback: 'Top strategy captures response'
      }
    )
  ),
  makeOcean(
    'con_009',
    "Complete your working philosophy: 'I prefer to finish tasks _____ rather than _____'",
    o5(
      'at the last minute / with a plan',
      'only when pressured / steadily',
      'when convenient / on schedule',
      'with checklists / from memory',
      'early and systematically / reactively'
    ),
    'WORD_BUILD',
    makeScene(
      'work_philosophy_fill',
      '#f4a261',
      {
        template: 'I prefer to finish tasks [ ] rather than [ ]',
        styleMeter: 'conscientiousness continuum'
      },
      {
        entry: 'Template line animates in',
        interaction: 'Choose best fitting phrase pair',
        feedback: 'Work-style meter updates'
      }
    )
  ),
  makeOcean(
    'con_010',
    'When lesson plans change unexpectedly, I adapt calmly with a backup approach.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'plan_change_response',
      '#f4a261',
      {
        panel1: 'Detailed plan ready',
        panel2: 'Unexpected disruption announced',
        panel3: 'Calm adaptation scale A-E'
      },
      {
        entry: 'Plan page crumple/fold transition',
        interaction: 'Pick response level',
        feedback: 'Backup plan animation reflects score'
      }
    )
  )
];
const oceanExtraversion = [
  makeOcean(
    'ext_001',
    'After a full day of teaching and meetings, I feel energised and ready for more social interaction.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'end_of_day_energy',
      '#6a4c93',
      {
        panel1: 'Busy school day montage',
        panel2: 'Colleagues invite after-hours event',
        panel3: 'Energy scale A-E'
      },
      {
        entry: 'Energy bar animates',
        interaction: 'Choose A-E',
        feedback: 'Bar drains/fills based on response'
      }
    )
  ),
  makeOcean(
    'ext_002',
    "A colleague says: 'Can you lead the whole-school assembly this Friday?' You think:",
    o5(
      'I strongly want to avoid this',
      'I would be uncomfortable doing it',
      'I could do it if needed',
      'I would be fairly happy to do it',
      'I would be genuinely excited to do it'
    ),
    'SPEECH_BUBBLE',
    makeScene(
      'assembly_invite',
      '#6a4c93',
      {
        hall: 'assembly stage with spotlight',
        colleaguePrompt: 'lead assembly request'
      },
      {
        entry: 'Stage spotlight lands on teacher',
        interaction: 'Select inner reaction option',
        feedback: 'Avatar confidence reaction'
      }
    )
  ),
  makeOcean(
    'ext_003',
    'Click the professional activity that sounds most appealing to you:',
    o5(
      'Solo curriculum planning (quiet office)',
      'Leading a parent workshop',
      'Marking assignments at home',
      'Facilitating teacher training group',
      'Reading research papers alone'
    ),
    'CHAOS_CLICK',
    makeScene(
      'activity_map',
      '#6a4c93',
      {
        map: 'school map with five activity nodes',
        nodes: ['solo_planning', 'parent_workshop', 'marking_home', 'training_group', 'reading_alone']
      },
      {
        entry: 'Activity nodes fade in',
        interaction: 'Click preferred activity zone',
        feedback: 'Chosen location becomes animated/lively'
      }
    )
  ),
  makeOcean(
    'ext_004',
    'I naturally take on leadership roles in group settings at school.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'group_leadership_gap',
      '#6a4c93',
      {
        panel1: 'Teacher group meeting',
        panel2: 'Leadership vacuum appears',
        panel3: 'A-E leadership tendency'
      },
      {
        entry: 'Spotlight sweeps table',
        interaction: 'Select scale',
        feedback: 'Spotlight shifts toward/away avatar'
      }
    )
  ),
  makeOcean(
    'ext_005',
    'How much do you enjoy classroom performance and presenting?',
    o5(
      'I avoid it whenever possible',
      'I tolerate it when necessary',
      'I am neutral about it',
      'I generally enjoy it',
      "It's my favorite part of teaching"
    ),
    'METER_FILL',
    makeScene(
      'presentation_enjoyment_meter',
      '#6a4c93',
      {
        stage: 'teacher + audience',
        meter: 'applause intensity'
      },
      {
        entry: 'Audience appears gradually',
        interaction: 'Fill enjoyment meter',
        feedback: 'Audience enthusiasm rises with level'
      }
    )
  ),
  makeOcean(
    'ext_006',
    'Sort these into: Energises Me vs Drains Me',
    o5(
      'Staff social events',
      'One-on-one student conferences',
      'Leading professional development',
      'Administrative paperwork',
      'Parent evenings / open days'
    ),
    'SORT_BIN',
    makeScene(
      'energy_sort',
      '#6a4c93',
      {
        bins: ['Energises Me', 'Drains Me'],
        battery: 'charge/discharge visual'
      },
      {
        entry: 'Battery icon and cards animate in',
        interaction: 'Drag cards into bins',
        feedback: 'Battery charge updates'
      }
    )
  ),
  makeOcean(
    'ext_007',
    'These weekend invitations arrive. Which do you respond to first?',
    o5(
      "Colleagues' dinner party (20 people)",
      'Quiet coffee with one close friend',
      'Large education conference',
      'Online webinar from home',
      'Team sports event with staff'
    ),
    'STRESS_INBOX',
    makeScene(
      'social_invitation_inbox',
      '#6a4c93',
      {
        inbox: 'weekend invite cards',
        meter: 'introvert-extrovert dial'
      },
      {
        entry: 'Invites stagger in from right',
        interaction: 'Open first invite',
        feedback: 'Meter shifts to preference'
      }
    )
  ),
  makeOcean(
    'ext_008',
    "I find it easy to strike up conversations with people I've just met at school events.",
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'new_people_conversation',
      '#6a4c93',
      {
        panel1: 'School networking event',
        panel2: 'Unknown person nearby',
        panel3: 'Comfort scale A-E'
      },
      {
        entry: 'Scene reveals with mild ambient crowd motion',
        interaction: 'Choose comfort level',
        feedback: 'Conversation bubble appears quickly/slowly'
      }
    )
  ),
  makeOcean(
    'ext_009',
    'Place when you feel most productive on a social energy timeline:',
    o5(
      'Completely alone',
      'Mostly alone',
      'Balanced social/solo',
      'Mostly social',
      'Highly social and collaborative'
    ),
    'TIMELINE_DROP',
    makeScene(
      'social_energy_timeline',
      '#6a4c93',
      {
        timeline: 'isolation to immersion',
        marker: 'productivity point'
      },
      {
        entry: 'Gradient timeline draws',
        interaction: 'Drop marker',
        feedback: 'Point locks with pulse'
      }
    )
  ),
  makeOcean(
    'ext_010',
    'Set the dial to your ideal team size for professional projects:',
    o5('Work alone', 'Pair work', 'Small group (3-4)', 'Medium team (5-7)', 'Large collaborative team (10+)'),
    'DIAL_TURN',
    makeScene(
      'team_size_dial',
      '#6a4c93',
      {
        dial: 'team size levels',
        svgTeam: 'figures appear/disappear'
      },
      {
        entry: 'Team seats animate in',
        interaction: 'Turn dial',
        feedback: 'Team count updates with animation'
      }
    )
  )
];
const oceanAgreeableness = [
  makeOcean(
    'agr_001',
    "A colleague's lesson plan has a significant error. Pulling lever = tell them directly. Do nothing = say nothing to keep peace.",
    o5(
      'Definitely say nothing',
      'Probably avoid direct feedback',
      'Unsure / depends',
      'Probably give direct constructive feedback',
      'Definitely give direct constructive feedback'
    ),
    'TROLLEY',
    makeScene(
      'honesty_vs_harmony',
      '#e63946',
      {
        tracks: ['harmony', 'honesty'],
        socialContext: 'peer relationship'
      },
      {
        entry: 'Two-track moral split appears',
        interaction: 'Select response intensity A-E',
        feedback: 'Colleague reaction animation'
      }
    )
  ),
  makeOcean(
    'agr_002',
    'I find it difficult to say no when colleagues ask for favours, even when I am overloaded.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'overload_favor_request',
      '#e63946',
      {
        panel1: 'Desk overloaded',
        panel2: 'Colleague asks for favor',
        panel3: 'Agreement A-E'
      },
      {
        entry: 'Task pile grows visually',
        interaction: 'Choose A-E',
        feedback: 'Load meter shifts'
      }
    )
  ),
  makeOcean(
    'agr_003',
    'A parent aggressively demands a grade change for their child. You say:',
    o5(
      'Agree immediately to avoid conflict',
      'Lean toward agreeing to calm situation',
      'Stay neutral and defer',
      'Set clear boundary with respectful explanation',
      'Firmly hold boundary and process requirements'
    ),
    'SPEECH_BUBBLE',
    makeScene(
      'grade_change_pressure',
      '#e63946',
      {
        parentTone: 'aggressive',
        boundaryVisual: 'wall builds or crumbles'
      },
      {
        entry: 'Parent bubble enters forcefully',
        interaction: 'Select response tone',
        feedback: 'Boundary visual updates'
      }
    )
  ),
  makeOcean(
    'agr_004',
    'Sort these into how you typically handle disagreements:',
    o5(
      'Avoid and let it pass',
      'Address it immediately',
      'Seek compromise',
      'Give in to maintain harmony',
      'Escalate to manager'
    ),
    'SORT_BIN',
    makeScene(
      'disagreement_styles',
      '#e63946',
      {
        bins: ['My Usual Style', 'Rarely Me'],
        cards: 5
      },
      {
        entry: 'Cards orbit then settle',
        interaction: 'Sort behavior cards',
        feedback: 'Conflict style meter updates'
      }
    )
  ),
  makeOcean(
    'agr_005',
    'How much do you prioritise group harmony over speaking your honest opinion?',
    o5(
      'Always honest, even if disruptive',
      'Usually honest over harmony',
      'Balanced',
      'Usually harmony over honesty',
      'Harmony always comes first'
    ),
    'METER_FILL',
    makeScene(
      'harmony_honesty_balance',
      '#e63946',
      {
        balance: 'group cohesion vs directness',
        indicator: 'team tension'
      },
      {
        entry: 'Balance scale fades in',
        interaction: 'Set level',
        feedback: 'Cohesion/tension scene shifts'
      }
    )
  ),
  makeOcean(
    'agr_006',
    'When a student is rude to me, I address it firmly but without personalising it.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'rude_student_response',
      '#e63946',
      {
        panel1: 'Student outburst',
        panel2: 'Teacher internal reaction',
        panel3: 'Firmness regulation A-E'
      },
      {
        entry: 'Emotion meter appears',
        interaction: 'Choose level',
        feedback: 'Regulation meter settles'
      }
    )
  ),
  makeOcean(
    'agr_007',
    "Your school's policy requires a failing grade for incomplete work. A struggling student submits late. You:",
    o5(
      'Apply policy strictly (fail the work)',
      'Give partial credit compassionately',
      'Accept late work with grade penalty',
      'Ignore policy, full marks for effort',
      'Escalate for exceptional review'
    ),
    'TROLLEY',
    makeScene(
      'policy_vs_compassion',
      '#e63946',
      {
        tracks: ['policy book', 'student file'],
        scale: 'policy-compassion balance'
      },
      {
        entry: 'Dual-track ethics scene appears',
        interaction: 'Choose path option',
        feedback: 'Scale tips with outcome reveal'
      }
    )
  ),
  makeOcean(
    'agr_008',
    'Order what matters most to you in professional relationships:',
    o5(
      'Being liked by colleagues',
      'Being respected professionally',
      'Being honest even if unpopular',
      'Keeping peace in all situations',
      'Advocating for students always'
    ),
    'PRIORITY_DRAG',
    makeScene(
      'relationship_values',
      '#e63946',
      {
        cards: 'five value cards',
        indicators: ['warmth', 'integrity', 'advocacy']
      },
      {
        entry: 'Cards glow with warmth/strength cues',
        interaction: 'Rank by priority',
        feedback: 'Top value captured'
      }
    )
  ),
  makeOcean(
    'agr_009',
    'Click the situation where you find it hardest to assert yourself:',
    o5(
      'Disagreeing with a senior colleague',
      "Correcting a parent's assumption",
      'Refusing an unreasonable student request',
      'Challenging an unfair school policy',
      'Saying no to a close friend at work'
    ),
    'CHAOS_CLICK',
    makeScene(
      'assertiveness_map',
      '#e63946',
      {
        map: 'five difficult assertiveness scenarios',
        meter: 'assertiveness strain'
      },
      {
        entry: 'Scenario nodes appear',
        interaction: 'Click hardest node',
        feedback: 'Meter response + lock-in'
      }
    )
  ),
  makeOcean(
    'agr_010',
    'I genuinely enjoy helping colleagues even when there is nothing in it for me.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'altruistic_helping',
      '#e63946',
      {
        panel1: 'Colleague struggling alone',
        panel2: 'No visible reward',
        panel3: 'Helping willingness A-E'
      },
      {
        entry: 'Panel warmth glow intro',
        interaction: 'Select willingness level',
        feedback: 'Generosity aura radiates'
      }
    )
  )
];

const oceanNeuroticism = [
  makeOcean(
    'neu_001',
    'These all arrive Monday morning. Rate your honest stress response:',
    o5(
      'Unannounced classroom observation today',
      'Angry parent email from Friday',
      'Report deadline moved to tomorrow',
      'New student with complex needs joining now',
      'IT system is down before your digital lesson'
    ),
    'STRESS_INBOX',
    makeScene(
      'monday_storm',
      '#e9c46a',
      {
        inbox: 'five stress notifications',
        meter: 'stress level rises with flood'
      },
      {
        entry: 'Notifications spiral in',
        interaction: 'Open/clear in sequence',
        feedback: 'Stress meter and avatar reaction'
      }
    )
  ),
  makeOcean(
    'neu_002',
    'How much does negative student feedback affect your mood for the rest of the day?',
    o5('Not at all', 'A little', 'Moderately', 'Strongly', 'Significantly, most of the day'),
    'METER_FILL',
    makeScene(
      'mood_barometer',
      '#e9c46a',
      {
        meter: 'mood barometer',
        weather: 'sun to storm by level'
      },
      {
        entry: 'Barometer rises from baseline',
        interaction: 'Set impact level',
        feedback: 'Weather transition'
      }
    )
  ),
  makeOcean(
    'neu_003',
    'Before a parent evening, I typically feel:',
    o5('Very calm', 'Mostly calm', 'Mixed', 'Quite anxious', 'Highly anxious'),
    'SCENARIO_PANEL',
    makeScene(
      'parent_evening_anticipation',
      '#e9c46a',
      {
        panel1: 'Calendar marks parent evening',
        panel2: 'Possible scenarios imagined',
        panel3: 'Anxiety scale A-E'
      },
      {
        entry: 'Thought bubbles multiply progressively',
        interaction: 'Select scale',
        feedback: 'Bubble density reflects anxiety'
      }
    )
  ),
  makeOcean(
    'neu_004',
    'Multiple problems arrive at once. Which makes you feel most overwhelmed?',
    o5(
      'Sudden schedule change',
      'Conflict between students',
      'Admin deadline escalation',
      'Parent escalation email',
      'Technical breakdown in class'
    ),
    'STRESS_INBOX',
    makeScene(
      'overwhelm_selector',
      '#e9c46a',
      {
        inbox: 'simultaneous stressors',
        cornerMeter: 'overwhelm'
      },
      {
        entry: 'Cards burst in rapidly',
        interaction: 'Select primary overwhelm trigger',
        feedback: 'Screen tremor intensity'
      }
    )
  ),
  makeOcean(
    'neu_005',
    'Set the dial to your typical emotional recovery time after a difficult lesson:',
    o5('Minutes', 'Few hours', 'By end of day', '1-2 days', 'Several days'),
    'DIAL_TURN',
    makeScene(
      'recovery_dial',
      '#e9c46a',
      {
        dial: 'recovery duration',
        avatar: 'bounce-back speed'
      },
      {
        entry: 'Recovery timeline appears',
        interaction: 'Turn dial',
        feedback: 'Avatar recovery animation speed'
      }
    )
  ),
  makeOcean(
    'neu_006',
    'I often replay difficult classroom moments in my mind long after they have passed.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'rumination_loop',
      '#e9c46a',
      {
        panel1: 'Difficult moment',
        panel2: 'Evening replay',
        panel3: 'Rumination frequency A-E'
      },
      {
        entry: 'Thought loop spiral appears',
        interaction: 'Select frequency',
        feedback: 'Spiral dissolves/intensifies'
      }
    )
  ),
  makeOcean(
    'neu_007',
    "Complete your stress response: 'When under pressure, I tend to _____ and feel _____'",
    o5(
      'focus and steady',
      'slow down and cautious',
      'fluctuate and uncertain',
      'rush and tense',
      'overwhelm and exhausted'
    ),
    'WORD_BUILD',
    makeScene(
      'pressure_phrase_builder',
      '#e9c46a',
      {
        template: 'When under pressure, I tend to [ ] and feel [ ]',
        gauges: ['pressure', 'coping']
      },
      {
        entry: 'Template and gauges animate in',
        interaction: 'Choose phrase profile',
        feedback: 'Gauges rebalance to selected style'
      }
    )
  ),
  makeOcean(
    'neu_008',
    'You receive these simultaneously on a Friday afternoon. What is your gut reaction?',
    o5(
      'Observation feedback form (mixed comments)',
      'Principal wants to meet Monday',
      'Three assignments need regrading',
      'A parent has CCd the principal on a complaint',
      'Budget cuts may affect your department'
    ),
    'STRESS_INBOX',
    makeScene(
      'friday_dread_inbox',
      '#e9c46a',
      {
        inbox: 'Friday stress stack',
        meter: 'dread vs calm'
      },
      {
        entry: 'Friday notifications cascade in',
        interaction: 'Open gut-trigger item',
        feedback: 'Dread meter update'
      }
    )
  ),
  makeOcean(
    'neu_009',
    'When I make a teaching mistake in front of students, I handle it with self-compassion and model learning from errors.',
    agreeScale,
    'SCENARIO_PANEL',
    makeScene(
      'mistake_recovery',
      '#e9c46a',
      {
        panel1: 'Visible teaching mistake',
        panel2: 'Students observe reaction',
        panel3: 'Self-compassion A-E'
      },
      {
        entry: 'Inner critic vs inner coach visuals',
        interaction: 'Select response level',
        feedback: 'Coach/critic dominance shifts'
      }
    )
  ),
  makeOcean(
    'neu_010',
    'Overall, how much does workplace stress affect your sleep and personal life?',
    o5('Rarely', 'Occasionally', 'Sometimes', 'Often', 'Almost every day'),
    'METER_FILL',
    makeScene(
      'work_home_boundary',
      '#e9c46a',
      {
        boundary: 'school-home stress boundary',
        particles: 'stress crossing indicator'
      },
      {
        entry: 'Boundary line draws in',
        interaction: 'Set impact level',
        feedback: 'Stress particles cross by intensity'
      }
    )
  )
];

const questionBank = {
  core: {
    pedagogical_foundations: pedagogicalFoundations,
    classroom_situation_handling: classroomSituationHandling,
    digital_literacy_edtech: digitalLiteracyEdtech,
    subject_academic_knowledge: subjectAcademicKnowledge,
    ethics_policy_leadership: ethicsPolicyLeadership
  },
  ocean: {
    Openness: oceanOpenness,
    Conscientiousness: oceanConscientiousness,
    Extraversion: oceanExtraversion,
    Agreeableness: oceanAgreeableness,
    Neuroticism: oceanNeuroticism
  }
};

const coreOrder = [
  'pedagogical_foundations',
  'classroom_situation_handling',
  'digital_literacy_edtech',
  'subject_academic_knowledge',
  'ethics_policy_leadership'
];

const oceanOrder = ['Openness', 'Conscientiousness', 'Extraversion', 'Agreeableness', 'Neuroticism'];

const sectionEntries = [
  ...coreOrder.map((key) => ({ group: 'core', key, questions: questionBank.core[key] || [] })),
  ...oceanOrder.map((key) => ({ group: 'ocean', key, questions: questionBank.ocean[key] || [] }))
];

const validationErrors = [];
const idSet = new Set();

sectionEntries.forEach(({ group, key, questions }) => {
  if (questions.length !== 10) {
    validationErrors.push(`${group}.${key} must contain exactly 10 questions, received ${questions.length}`);
  }

  let streakType = null;
  let streak = 0;

  questions.forEach((question, index) => {
    if (idSet.has(question.id)) {
      validationErrors.push(`Duplicate question id: ${question.id}`);
    }
    idSet.add(question.id);

    const optionCount = Array.isArray(question.options) ? question.options.length : 0;
    const expectedOptions = group === 'core' ? 4 : 5;
    if (optionCount !== expectedOptions) {
      validationErrors.push(`${question.id} must have ${expectedOptions} options, received ${optionCount}`);
    }

    if (group === 'core') {
      if (!question.correct) {
        validationErrors.push(`${question.id} is missing core 'correct' answer label`);
      } else if (!question.options.some((option) => option.label === question.correct)) {
        validationErrors.push(`${question.id} correct label '${question.correct}' is not in options`);
      }
    } else if (question.correct) {
      validationErrors.push(`${question.id} must not include 'correct' because it is an OCEAN question`);
    }

    if (!question.gameType) {
      validationErrors.push(`${question.id} is missing gameType`);
    }
    if (!question.scene || !question.scene.type || !question.scene.accent) {
      validationErrors.push(`${question.id} scene must include type and accent`);
    }

    if (question.gameType === streakType) {
      streak += 1;
      if (streak > 2) {
        validationErrors.push(`${group}.${key} has more than 2 consecutive '${question.gameType}' at index ${index}`);
      }
    } else {
      streakType = question.gameType;
      streak = 1;
    }
  });
});

const totalCount = sectionEntries.reduce((sum, entry) => sum + entry.questions.length, 0);
if (totalCount !== 100) {
  validationErrors.push(`Total question count must be 100, received ${totalCount}`);
}

const viteEnv =
  typeof import.meta !== 'undefined' && import.meta && import.meta.env
    ? import.meta.env
    : null;
const isDev = viteEnv
  ? !viteEnv.PROD
  : typeof process !== 'undefined'
    ? process.env.NODE_ENV !== 'production'
    : true;

if (isDev && validationErrors.length > 0) {
  throw new Error(
    `Gamified questionBank validation failed:\n- ${validationErrors.join('\n- ')}`
  );
}

const flatQuestions = sectionEntries.flatMap((entry) => entry.questions);
const questionById = flatQuestions.reduce((acc, question) => {
  acc[question.id] = question;
  return acc;
}, {});

const banks = {
  pedagogical: questionBank.core.pedagogical_foundations,
  classroom: questionBank.core.classroom_situation_handling,
  digital: questionBank.core.digital_literacy_edtech,
  subject: questionBank.core.subject_academic_knowledge,
  ethics: questionBank.core.ethics_policy_leadership,
  openness: questionBank.ocean.Openness,
  conscientiousness: questionBank.ocean.Conscientiousness,
  extraversion: questionBank.ocean.Extraversion,
  agreeableness: questionBank.ocean.Agreeableness,
  neuroticism: questionBank.ocean.Neuroticism
};

export { questionBank, flatQuestions, questionById, banks };
export default questionBank;
